import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

import Icon from '../../../components/AppIcon';

const TimelineSection = ({ data, onChange, errors }) => {
  const [newMilestone, setNewMilestone] = useState({
    title: '',
    description: '',
    dueDate: '',
    deliverables: ''
  });

  const durationOptions = [
    { value: '4-weeks', label: '4 Weeks' },
    { value: '8-weeks', label: '8 Weeks' },
    { value: '12-weeks', label: '12 Weeks' },
    { value: '16-weeks', label: '16 Weeks' },
    { value: '6-months', label: '6 Months' },
    { value: 'custom', label: 'Custom Duration' }
  ];

  const urgencyOptions = [
    { value: 'immediate', label: 'Immediate (Within 1 month)' },
    { value: 'high', label: 'High (Within 3 months)' },
    { value: 'medium', label: 'Medium (Within 6 months)' },
    { value: 'low', label: 'Low (Within 1 year)' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const addMilestone = () => {
    if (newMilestone?.title && newMilestone?.dueDate) {
      const milestones = data?.milestones || [];
      onChange('milestones', [...milestones, { ...newMilestone, id: Date.now() }]);
      setNewMilestone({
        title: '',
        description: '',
        dueDate: '',
        deliverables: ''
      });
    }
  };

  const removeMilestone = (id) => {
    const milestones = data?.milestones || [];
    onChange('milestones', milestones?.filter(m => m?.id !== id));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateProjectEndDate = () => {
    if (!data?.projectStartDate || !data?.projectDuration) return '';
    
    const startDate = new Date(data.projectStartDate);
    let endDate = new Date(startDate);
    
    switch (data?.projectDuration) {
      case '4-weeks':
        endDate?.setDate(startDate?.getDate() + 28);
        break;
      case '8-weeks':
        endDate?.setDate(startDate?.getDate() + 56);
        break;
      case '12-weeks':
        endDate?.setDate(startDate?.getDate() + 84);
        break;
      case '16-weeks':
        endDate?.setDate(startDate?.getDate() + 112);
        break;
      case '6-months':
        endDate?.setMonth(startDate?.getMonth() + 6);
        break;
      default:
        return '';
    }
    
    return endDate?.toISOString()?.split('T')?.[0];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
          <Icon name="Calendar" size={16} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Timeline & Milestones</h2>
          <p className="text-sm text-muted-foreground">Set project duration and key deadlines</p>
        </div>
      </div>
      {/* Project Timeline */}
      <div className="bg-muted rounded-lg p-4 space-y-4">
        <h3 className="text-lg font-medium text-foreground">Project Timeline</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Project Duration"
            placeholder="Select duration"
            options={durationOptions}
            value={data?.projectDuration || ''}
            onChange={(value) => onChange('projectDuration', value)}
            error={errors?.projectDuration}
            required
          />

          <Select
            label="Urgency Level"
            placeholder="Select urgency"
            options={urgencyOptions}
            value={data?.urgencyLevel || ''}
            onChange={(value) => onChange('urgencyLevel', value)}
            description="How quickly do you need this solved?"
          />

          <Input
            label="Preferred Start Date"
            type="date"
            value={data?.projectStartDate || ''}
            onChange={(e) => onChange('projectStartDate', e?.target?.value)}
            error={errors?.projectStartDate}
            required
            min={new Date()?.toISOString()?.split('T')?.[0]}
          />

          <Input
            label="Expected End Date"
            type="date"
            value={data?.projectEndDate || calculateProjectEndDate()}
            onChange={(e) => onChange('projectEndDate', e?.target?.value)}
            description="Auto-calculated based on duration"
            disabled={data?.projectDuration !== 'custom'}
          />
        </div>

        {data?.projectDuration === 'custom' && (
          <Input
            label="Custom Duration Description"
            type="text"
            placeholder="e.g., 10 weeks with 2-week break"
            value={data?.customDuration || ''}
            onChange={(e) => onChange('customDuration', e?.target?.value)}
            description="Describe your custom timeline requirements"
          />
        )}
      </div>
      
      {/* Additional Timeline Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Additional Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Seasonal Considerations
            </label>
            <textarea
              className="w-full min-h-20 p-3 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y"
              placeholder="Any seasonal factors that might affect the project timeline..."
              value={data?.seasonalConsiderations || ''}
              onChange={(e) => onChange('seasonalConsiderations', e?.target?.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Flexibility Requirements
            </label>
            <textarea
              className="w-full min-h-20 p-3 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y"
              placeholder="How flexible are the deadlines? Any hard constraints..."
              value={data?.flexibilityRequirements || ''}
              onChange={(e) => onChange('flexibilityRequirements', e?.target?.value)}
            />
          </div>
        </div>

        <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} className="text-accent mt-0.5" />
            <div>
              <h4 className="font-medium text-foreground mb-1">Timeline Recommendations</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Allow 2-3 weeks for team formation and initial planning</li>
                <li>• Include buffer time for testing and iterations</li>
                <li>• Consider academic calendar constraints</li>
                <li>• Plan for regular check-ins and feedback sessions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineSection;